import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
	CellValueChangedEvent,
	ColDef,
	GridApi,
	GridReadyEvent,
	SideBarDef,
} from 'ag-grid-community';
import { FormControl, FormGroup } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BankingPortfolioService } from '@lmc-app/core/services/banking-portfolio.service';
import { LoanAccountService } from '@lmc-app/core/services/loan-account.service';
import { CurrencyPipe } from '@angular/common';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { FundingListToolPanelComponent } from '@lmc-app/custom-table/funding-list-tool-panel/funding-list-tool-panel.component';
import { Subject, Subscription } from 'rxjs';
import { Loading } from '../../applications/loading';

import 'ag-grid-enterprise';

@Component({
	selector: 'app-funding-list-dashboard',
	templateUrl: './funding-list-dashboard.component.html',
	styleUrls: ['./funding-list-dashboard.component.scss'],
})
export class FundingListDashboardComponent implements OnInit, OnDestroy {
	private parentSub = new Subscription();
	private readonly _onDestroy = new Subject<void>();

	protected readonly inputDateForm = new FormGroup({
		input_date: new FormControl(null),
	});

	public readonly headerStyle: any = {
		'display': 'flex',
		'justify-content': 'space-between',
	};

	public readonly title: string = "Funding List Dashboard";

	public columnDefs: ColDef[] = [
		{
			field: 'si',
			headerName: 'SI',
			width: 50,
			pinned: 'left',
		},
		{
			field: 'loan_number',
			headerName: 'Loan Number',
			width: 150,
			pinned: 'left',
		},
		{
			field: 'name',
			headerName: 'AC NO',
			width: 250,
			pinned: 'left',
			cellStyle: params => {
				let style = null;
				switch (params.data.status) {
					case "Validated":
					case "Funded":
						style = { backgroundColor: "#3aff17" };
						break;
					default:
						break;
				}
				return style;
			},
		},
		{
			headerName: 'SAHA CHQ',
			width: 130,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data["SAHA CHQ"],
					'CAD',
					'symbol-narrow'
				);
			},
		},
		{
			headerName: 'Portfolio',
			width: 120,
			valueGetter: (params) => params.data.bankingPortfolio?.name,
		},
		{
			headerName: 'Amount',
			width: 120,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data.amount,
					'CAD',
					'symbol-narrow'
				);
			},
			cellStyle: params => {
				let style = {};
				if (params.data.amount !== params.data.original_note_amount) {
					style = { color: "red" };
				}
				switch (params.data.status) {
					case "Funded":
						style = { ...style, backgroundColor: "#3aff17" };
						break;
					default:
						break;
				}
				return style;
			},
		},
		{
			field: 'exception_note',
			headerName: 'Exception',
			width: 150,
			cellStyle: params => params.value ? { backgroundColor: "#fc9d9d" } : null,
		},
		{
			field: 'LPP',
			headerName: 'LPP',
			width: 80,
		},
		{
			field: 'application_source',
			headerName: 'OBS',
			width: 100,
		},
		{
			field: 'rejection_note',
			headerName: 'Rejection',
			width: 200,
		},
		{
			field: 'status',
			headerName: 'Status',
			width: 160,
		},
	];

	private bankingPortfolios: IdName[] = [];
	protected rowData: { status: string }[] = []
	private sum = {
		"SAHA CHQ": 0,
	}

	private readonly _bottomSheet = inject(MatBottomSheet);
	private readonly _snackBar = inject(MatSnackBar);

	public sideBar: SideBarDef | string | string[] | boolean | null;

	public constructor(
		private readonly bankingPortfolioService: BankingPortfolioService,
		private readonly loanAccountService: LoanAccountService,
		private readonly currencyFormatter: CurrencyPipe,
	) { }

	public ngOnInit(): void {
		this.setFundingAccount([]);
		this.inputDateForm.controls.input_date.setValue(new Date());
		this.getPortfolios();
		this.getFundingList(this.inputDateForm.controls.input_date.value.toISOString().substring(0, 10));
	}

	public ngOnDestroy(): void {
		this.parentSub.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	private getPortfolios() {
		this.parentSub.add(this.bankingPortfolioService.getBankingPortfolios().subscribe({
			next: (res) => {
				this.bankingPortfolios = res;
				this.columnDefs = this.columnDefs.map(colDef => {
					if (colDef.headerName === "Portfolio") {
						colDef.cellEditorParams.values = ['', ...this.bankingPortfolios.map(bankingPortfolio => bankingPortfolio.name)];
					}
					return colDef;
				});
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	protected onInputDateChanged(inputDate: Date) {
		this.getFundingList(inputDate.toISOString().substring(0, 10));
	}

	protected onCellValueChanged(e: CellValueChangedEvent<any>) {
		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Saving' },
		});
		const loanAccount = {
			...e.data,
			banking_portfolio_id: e.data.bankingPortfolio?.banking_portfolio_id,
		};
		this.parentSub.add(this.loanAccountService.updateLoanAccount(loanAccount).subscribe({
			next: () => {
				this.getFundingList(this.inputDateForm.controls.input_date.value.toISOString().substring(0, 10))
				this._snackBar.open(
					`Loan account for ${loanAccount.name} is updated.`,
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
				this._bottomSheet.dismiss();
			},
			error: (error) => {
				this.getFundingList(this.inputDateForm.controls.input_date.value.toISOString().substring(0, 10))
				this._bottomSheet.dismiss();
				console.error(error);
				this._snackBar.open(
					(error.error && (error.error.message && (error.error.message.name || error.error.message))) || 'Error updating loan account.',
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
			},
		}));
	}

	private getFundingList(inputDate: string) {
		this.parentSub.add(this.loanAccountService.getLoanAccounts(inputDate).subscribe((res: any) => {
			if (res.length) {
				this.loanAccountService.getDailyTarget(inputDate).subscribe((resDailyTarget: any) => {
					this.setFundingAccount(res, resDailyTarget["daily_target"]);
				});
			} else {
				this.setFundingAccount([]);
			}
		}));
	}

	private setFundingAccount(data: { status: string, "SAHA CHQ": number, bankingPortfolio: { name: string }, amount: number }[], dailyTarget = undefined) {
		this.rowData = data;
		const fundedList = data.filter(deal => deal.status === 'Funded');
		this.sum = {
			"SAHA CHQ": 0,
		};
		this.bankingPortfolios.forEach(portfolio => {
			this.sum[portfolio.name] = fundedList.filter(loanAccount => loanAccount.bankingPortfolio?.name === portfolio.name).reduce((acc, curr) => acc + curr.amount, 0);
		});
		this.sum["Portfolio Total"] = Object.values(this.sum).reduce((acc, cur) => acc + cur, 0);
		this.sum["SAHA CHQ"] = fundedList.reduce((acc, curr) => acc + curr["SAHA CHQ"], 0);
		this.sum["Agency Referal Total"] = this.sum["Portfolio Total"] - this.sum["SAHA CHQ"];
		if (dailyTarget) {
			this.sum["Daily Target"] = dailyTarget;
			this.sum["RFR"] = dailyTarget - this.sum["Portfolio Total"];
		}
		this.setSummaryData();
	}

	private setSummaryData() {
		const summaryData = [];
		for (const [label, amount] of Object.entries(this.sum)) {
			summaryData.push({
				label,
				value: this.currencyFormatter.transform(amount, 'CAD', 'symbol-narrow'),
				primary: label === "Portfolio Total",
			});
		}
		this.sideBar = {
			toolPanels: [
				{
					id: 'filters',
					labelDefault: 'Filters',
					labelKey: 'filters',
					iconKey: 'filter',
					toolPanel: 'agFiltersToolPanel',
				},
				{
					id: 'columns',
					labelDefault: 'Columns',
					labelKey: 'columns',
					iconKey: 'columns',
					toolPanel: 'agColumnsToolPanel',
					toolPanelParams: {
						suppressPivotMode: true,
						suppressValues: true,
						suppressRowGroups: true,
					},
				},
				{
					id: 'customStats',
					labelDefault: 'Summary',
					labelKey: 'customStats',
					iconKey: 'chart',
					width: 280,
					toolPanel: FundingListToolPanelComponent,
					toolPanelParams: {
						summaryData,
						footnote: "*RFR: Remaining Funding Requirement"
					},
				},
			],
			defaultToolPanel: 'customStats',
		};
	}

	private gridApi: GridApi<any>;

	protected filterValue: string = "";
	protected tooltipShowDelay = 0;
	protected tooltipHideDelay = 2000;

	protected overlayLoadingTemplate =
		'<span class="ag-overlay-loading-center">Loading Data...</span>';
	protected overlayNoRowsTemplate =
		'<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">This is a custom \'no rows\' overlay</span>';

	protected defaultColDef: ColDef = {
		sortable: true,
		resizable: true,
		filter: true,
		wrapHeaderText: true,
		autoHeaderHeight: true,
	};

	protected onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		this.gridApi.showLoadingOverlay();
	}

	protected onFilterTextBoxChanged() {
		this.gridApi.setQuickFilter(this.filterValue);
	}

	protected onFilterClear() {
		this.filterValue = '';
		this.onFilterTextBoxChanged()
	}

	protected resetFilter() {
		this.gridApi?.setFilterModel(null);
		this.onFilterClear();
	}
}
